import { AccountType } from '../shared/types'
import {
    Step1ValueModel,
    Step4ValueModel,
    Step5ValueModel,
} from '../shared/CustomerFormSteps/types'
import { Step2ValueModel, Step3ValueModel } from './types'
import { Section } from '../shared/Summary/types'
import { ISubmitRestaurantRegistrationBody } from "@apps/Shared/Services/CompanyRegistrations/interfaces";

export function getSubmitRestaurantRegistrationBody(
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel &
        Step5ValueModel
): ISubmitRestaurantRegistrationBody {
    return {
        GDPRConsentText: values.termsText,
        organizationNumber: values.organizationNumber,
        newOwnerForExistingStore: values.changeOwner === 'true' ? true : false,
        newOwnerForExistingStoreChangeDate: values.changeOwnerDate,
        municipalityCode: values.municipality
            ? Number(values.municipality.value)
            : -1,
        name: values.legalName,
        customerSiteName: values.restaurantName,
        unitName: values.unitName,
        hasFTax: values.registeredForFTax === 'true' ? true : false,
        hasVAT: values.registeredForVAT === 'true' ? true : false,
        vatNumber: values.vatNumber,
        accountType: values.accountType,
        accountNumber:
            values.accountType === AccountType.Bg ? values.bg : values.pg,
        ownerPostalAddress: {
            street1: values.careOf,
            street2: values.streetBox,
            zipCode: values.zipCode,
            city: values.city,
        },
        contactPerson: values.contactPerson
            ? {
                  firstname: values.contactPerson.firstname,
                  lastname: values.contactPerson.lastname,
                  email: values.contactPerson.email,
                  mobileNumber: values.contactPerson.mobile,
                  phoneNumber: values.contactPerson.phone,
              }
            : {},
        monthlyReportPerson: values.reportPerson
            ? {
                  firstname: values.reportPerson.firstname,
                  lastname: values.reportPerson.lastname,
                  email: values.reportPerson.email,
                  mobileNumber: values.reportPerson.mobile,
                  phoneNumber: values.reportPerson.phone,
              }
            : {},
        message: values.message,
        visitingAddressIsAlsoPostalAddress:
            (values.visitingAddress &&
                !!values.visitingAddress.isAlsoPostalAddress) ||
            false,
        visitingAddress: values.visitingAddress
            ? {
                  street1: values.visitingAddress.streetAddress,
                  zipCode: values.visitingAddress.zipCode,
                  city: values.visitingAddress.city,
              }
            : {},
        postalAddress:
            values.visitingAddress &&
            !!values.visitingAddress.isAlsoPostalAddress
                ? {
                      street1: values.visitingAddress.streetAddress,
                      zipCode: values.visitingAddress.zipCode,
                      city: values.visitingAddress.city,
                  }
                : {
                      street1: values.postalAddress.streetBox,
                      zipCode: values.postalAddress.zipCode,
                      city: values.postalAddress.city,
                  },
    }
}

export function getSections(
    t: Function,
    values: Step1ValueModel &
        Step2ValueModel &
        Step3ValueModel &
        Step4ValueModel
): Section[] {
    function formatBooleanString(value: any) {
        return value === 'true'
            ? t('global.yes').toUpperCase()
            : t('global.no').toUpperCase()
    }

    return [
        {
            title: t('companyRegistration.customer.restaurant.stepLabel.step2'),
            subsections: [
                {
                    title: '',
                    items: [
                        {
                            title: t(
                                'companyRegistration.organisationNumberLabel'
                            ),
                            value: values.organizationNumber,
                        },
                        {
                            title: t(
                                'companyRegistration.customer.changeOwnerLabel'
                            ),
                            value: formatBooleanString(values.changeOwner),
                        },
                        {
                            title: t(
                                'companyRegistration.customer.changeOwnerDate'
                            ),
                            value: values.changeOwnerDate,
                            hidden: values.changeOwner !== 'true',
                        },
                        {
                            title: t('companyRegistration.customer.legalName'),
                            value: values.legalName,
                        },
                        {
                            title: t('companyRegistration.fTaxLabel'),
                            value: formatBooleanString(
                                values.registeredForFTax
                            ),
                        },
                        {
                            title: t(
                                'companyRegistration.registeredForVatLabel'
                            ),
                            value: formatBooleanString(values.registeredForVAT),
                        },
                        {
                            title: t('companyRegistration.customer.vatNumber'),
                            value: values.vatNumber,
                            hidden: values.registeredForVAT !== 'true',
                        },
                        {
                            title: t(
                                'companyRegistration.customer.accountType'
                            ),
                            value:
                                values.accountType === AccountType.Bg
                                    ? values.bg
                                    : values.pg,
                        },
                    ],
                },
                {
                    title: t('companyRegistration.customer.postalAddress'),
                    items: [
                        {
                            title: t('companyRegistration.customer.careOf'),
                            value: values.careOf,
                        },
                        {
                            title: t('companyRegistration.customer.streetBox'),
                            value: values.streetBox,
                        },
                        {
                            title: t('companyRegistration.zipCode'),
                            value: values.zipCode,
                        },
                        {
                            title: t('companyRegistration.city'),
                            value: values.city,
                        },
                    ],
                },
            ],
        },
        {
            title: t('companyRegistration.customer.restaurant.stepLabel.step3'),
            subsections: [
                {
                    title: '',
                    items: [
                        {
                            title: t(
                                'companyRegistration.customer.restaurant.restaurantName'
                            ),
                            value: values.restaurantName,
                        },
                        {
                            title: t('companyRegistration.customer.unitName'),
                            value: values.unitName,
                        },
                        {
                            title: t(
                                'companyRegistration.customer.municipality'
                            ),
                            value:
                                (values.municipality &&
                                    values.municipality.label) ||
                                '',
                        },
                    ],
                },
                {
                    title: t('companyRegistration.customer.visitingAddress'),
                    items: [
                        {
                            title: t('companyRegistration.streetAddress'),
                            value:
                                values.visitingAddress &&
                                values.visitingAddress.streetAddress,
                        },
                        {
                            title: t('companyRegistration.zipCode'),
                            value:
                                values.visitingAddress &&
                                values.visitingAddress.zipCode,
                        },
                        {
                            title: t('companyRegistration.city'),
                            value:
                                values.visitingAddress &&
                                values.visitingAddress.city,
                        },
                    ],
                },
                values.visitingAddress &&
                values.visitingAddress.isAlsoPostalAddress
                    ? {
                          title: t(
                              'companyRegistration.customer.postalAddress'
                          ),
                          items: [
                              {
                                  title: t('companyRegistration.streetAddress'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.streetAddress,
                              },
                              {
                                  title: t('companyRegistration.zipCode'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.zipCode,
                              },
                              {
                                  title: t('companyRegistration.city'),
                                  value:
                                      values.visitingAddress &&
                                      values.visitingAddress.city,
                              },
                          ],
                      }
                    : {
                          title: t(
                              'companyRegistration.customer.postalAddress'
                          ),
                          items: [
                              {
                                  title: t(
                                      'companyRegistration.customer.streetBox'
                                  ),
                                  value:
                                      values.postalAddress &&
                                      values.postalAddress.streetBox,
                              },
                              {
                                  title: t('companyRegistration.zipCode'),
                                  value:
                                      values.postalAddress &&
                                      values.postalAddress.zipCode,
                              },
                              {
                                  title: t('companyRegistration.city'),
                                  value:
                                      values.postalAddress &&
                                      values.postalAddress.city,
                              },
                          ],
                      },
                {
                    title: t('companyRegistration.customer.messageForRp'),
                    items: [
                        {
                            title: t('companyRegistration.customer.message'),
                            value: values.message,
                        },
                    ],
                },
            ],
        },
        {
            title: t('companyRegistration.customer.restaurant.stepLabel.step4'),
            subsections: [
                {
                    title: t('companyRegistration.customer.contactPerson'),
                    slim: true,
                    items: [
                        {
                            title: t('global.firstname'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.firstname,
                        },
                        {
                            title: t('global.lastname'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.lastname,
                        },
                        {
                            title: t('global.email'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.email,
                        },
                        {
                            title: t('global.mobileNumber'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.mobile,
                        },
                        {
                            title: t('global.phoneNumber'),
                            value:
                                values.contactPerson &&
                                values.contactPerson.phone,
                        },
                    ],
                },
                {
                    title: t('companyRegistration.customer.reportPerson'),
                    slim: true,
                    items: [
                        {
                            title: t('global.firstname'),
                            value:
                                values.reportPerson &&
                                values.reportPerson.firstname,
                        },
                        {
                            title: t('global.lastname'),
                            value:
                                values.reportPerson &&
                                values.reportPerson.lastname,
                        },
                        {
                            title: t('global.email'),
                            value:
                                values.reportPerson &&
                                values.reportPerson.email,
                        },
                        {
                            title: t('global.mobileNumber'),
                            value:
                                values.reportPerson &&
                                values.reportPerson.mobile,
                        },
                        {
                            title: t('global.phoneNumber'),
                            value:
                                values.reportPerson &&
                                values.reportPerson.phone,
                        },
                    ],
                },
            ],
        },
    ]
}